import { CircularProgress, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderPlus, faSync, faUpload, faChevronRight, faChevronDown, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAiAssistant } from '../../AiAssistantContext';
import * as api from '../../api';
import { IFileNode } from '../../types';
import FileTreeNode from './FileTreeNode';
import InlineConfirmDelete from '../shared/InlineConfirmDelete';

function filterNodes(nodes: IFileNode[], query: string): IFileNode[] {
  if (!query) return nodes;
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return nodes;
  return nodes.reduce<IFileNode[]>((acc, node) => {
    const name = node.name.toLowerCase();
    if (words.every((w) => name.includes(w))) {
      acc.push(node);
    } else if (node.type === 'directory' && node.children) {
      const filtered = filterNodes(node.children, query);
      if (filtered.length > 0) {
        acc.push({ ...node, children: filtered });
      }
    }
    return acc;
  }, []);
}

function sortNodes(nodes: IFileNode[]): IFileNode[] {
  return [...nodes].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

interface FilesPanelProps {
  collapsed: boolean;
  onToggle: () => void;
}

const FilesPanel: React.FC<FilesPanelProps> = ({ collapsed, onToggle }) => {
  const { state, dispatch } = useAiAssistant();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const projectId = state.selectedProjectId;
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [creatingRoot, setCreatingRoot] = useState(false);
  const [rootFolderName, setRootFolderName] = useState('');
  const rootInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [manualRefresh, setManualRefresh] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ path: string; isDir: boolean } | null>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);

  const refreshFiles = async (manual = false) => {
    if (!projectId) return;
    if (manual) setManualRefresh(true);
    dispatch({ type: 'SET_LOADING_FILES', loading: true });
    try {
      const files = await api.fetchFileTree(projectId);
      dispatch({ type: 'SET_FILE_TREE', fileTree: Array.isArray(files) ? files : [] });
    } catch {
      dispatch({ type: 'SET_LOADING_FILES', loading: false });
      setError('Failed to load files');
    }
    setManualRefresh(false);
  };

  // Auto-refresh when the agent modifies files
  useEffect(() => {
    if (state.agentFileChangeSeq > 0) {
      refreshFiles();
    }
  }, [state.agentFileChangeSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!projectId || !e.target.files?.[0]) return;
    setUploading(true);
    setError('');
    try {
      await api.uploadFile(projectId, e.target.files[0]);
      await refreshFiles();
    } catch {
      setError('Upload failed');
    }
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileClick = (path: string, name: string) => {
    const tabId = `file:${path}`;
    dispatch({
      type: 'OPEN_TAB',
      tab: { id: tabId, type: 'file', label: name, filePath: path, projectId: projectId! },
    });
  };

  const handleRename = async (oldPath: string, newName: string) => {
    if (!projectId) return;
    setError('');
    try {
      const result = await api.renameFile(projectId, oldPath, newName);
      if (result.data) {
        dispatch({
          type: 'RENAME_FILE_PATH',
          oldPath: result.data.oldPath,
          newPath: result.data.newPath,
          newLabel: newName,
        });
      }
      await refreshFiles();
    } catch {
      setError('Rename failed');
    }
  };

  const handleDelete = async (path: string, isDir: boolean) => {
    if (!projectId) return;
    setPendingDelete({ path, isDir });
  };

  const confirmDelete = async () => {
    if (!projectId || !pendingDelete) return;
    const { path, isDir } = pendingDelete;
    setPendingDelete(null);
    setError('');
    try {
      if (isDir) {
        await api.deleteDirectory(projectId, path);
        state.tabs.forEach((t) => {
          if (t.type === 'file' && t.filePath && (t.filePath === path || t.filePath.startsWith(path + '/'))) {
            dispatch({ type: 'CLOSE_TAB', tabId: t.id });
          }
        });
      } else {
        await api.deleteFile(projectId, path);
        dispatch({ type: 'CLOSE_TAB', tabId: `file:${path}` });
      }
      await refreshFiles();
    } catch {
      setError('Delete failed');
    }
  };

  const handleCreateFolder = async (parentPath: string, name: string) => {
    if (!projectId) return;
    const fullPath = parentPath ? `${parentPath}/${name}` : name;
    setError('');
    try {
      await api.createDirectory(projectId, fullPath);
      await refreshFiles();
    } catch {
      setError('Failed to create folder');
    }
  };

  const handleCreateFile = async (parentPath: string, name: string) => {
    if (!projectId) return;
    const fullPath = parentPath ? `${parentPath}/${name}` : name;
    setError('');
    try {
      await api.createFile(projectId, fullPath);
      await refreshFiles();
    } catch {
      setError('Failed to create file');
    }
  };

  const handleDownload = async (path: string, name: string) => {
    if (!projectId) return;
    try {
      const result: any = await api.fetchFileContent(projectId, path);
      const content = typeof result === 'string' ? result : (result?.content ?? '');

      let blob: Blob;
      if (content.startsWith('data:')) {
        const [header, base64] = content.split(',');
        const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
        const raw = atob(base64);
        const bytes = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
        blob = new Blob([bytes], { type: mime });
      } else {
        blob = new Blob([content], { type: 'text/plain' });
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed');
    }
  };

  const handleRootFolderCreate = async () => {
    const trimmed = rootFolderName.trim();
    if (trimmed && projectId) {
      setError('');
      try {
        await api.createDirectory(projectId, trimmed);
        await refreshFiles();
      } catch {
        setError('Failed to create folder');
      }
    }
    setCreatingRoot(false);
    setRootFolderName('');
  };

  const handleRootFolderKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRootFolderCreate();
    } else if (e.key === 'Escape') {
      setCreatingRoot(false);
      setRootFolderName('');
    }
  };

  const startRootFolder = () => {
    setCreatingRoot(true);
    setRootFolderName('');
    setTimeout(() => rootInputRef.current?.focus(), 0);
  };

  const handleMove = async (sourcePath: string, destinationDir: string) => {
    if (!projectId) return;
    setError('');
    try {
      const result = await api.moveFile(projectId, sourcePath, destinationDir);
      if (result.data) {
        dispatch({
          type: 'RENAME_FILE_PATH',
          oldPath: result.data.oldPath,
          newPath: result.data.newPath,
          newLabel: result.data.newPath.split('/').pop() || '',
        });
      }
      await refreshFiles();
    } catch {
      setError('Move failed');
    }
  };

  const handleMoveToProject = async (sourcePath: string, targetProjectId: number) => {
    if (!projectId) return;
    setError('');
    try {
      const result = await api.moveFileToProject(projectId, sourcePath, targetProjectId);
      if (result.data) {
        state.tabs.forEach((t) => {
          if (t.type === 'file' && t.projectId === projectId && t.filePath &&
              (t.filePath === sourcePath || t.filePath.startsWith(sourcePath + '/'))) {
            dispatch({ type: 'CLOSE_TAB', tabId: t.id });
          }
        });
        await refreshFiles();
      } else {
        setError(result.message || 'Move to project failed');
      }
    } catch {
      setError('Move to project failed');
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    // Internal tree drag — move to root
    const treePath = e.dataTransfer.getData('application/x-file-tree-path');
    if (treePath) {
      await handleMove(treePath, '.');
      return;
    }
    // External file drop — upload
    if (!projectId || !e.dataTransfer.files.length) return;
    const files = Array.from(e.dataTransfer.files);
    setUploading(true);
    setError('');
    let failed = false;
    for (const file of files) {
      const result = await api.uploadFile(projectId, file);
      if (!result.data) {
        failed = true;
      }
    }
    if (failed) {
      setError('Upload failed');
    }
    await refreshFiles();
    setUploading(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragging(false);
  };

  if (!projectId) {
    return (
      <div>
        <SectionHeader onClick={onToggle}>
          <HeaderLeft>
            <ChevronIcon icon={collapsed ? faChevronRight : faChevronDown} />
            <SectionTitle>Files</SectionTitle>
          </HeaderLeft>
        </SectionHeader>
        {!collapsed && <EmptyText>Select a project</EmptyText>}
      </div>
    );
  }

  const otherProjects = state.projects
    .filter((p) => p.id !== projectId)
    .map((p) => ({ id: p.id, name: p.name }));

  const treeProps = {
    onFileClick: handleFileClick,
    onRename: handleRename,
    onDelete: handleDelete,
    onCreateFolder: handleCreateFolder,
    onCreateFile: handleCreateFile,
    onDownload: handleDownload,
    onMove: handleMove,
    onMoveToProject: handleMoveToProject,
    otherProjects,
  };

  return (
    <DropZone
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      $active={dragging}
    >
      <SectionHeader onClick={onToggle}>
        <HeaderLeft>
          <ChevronIcon icon={collapsed ? faChevronRight : faChevronDown} />
          <SectionTitle>Files</SectionTitle>
        </HeaderLeft>
        <ButtonGroup>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowFilter(!showFilter); if (showFilter) setFilterText(''); else setTimeout(() => filterInputRef.current?.focus(), 0); }} title="Filter">
            <FontAwesomeIcon icon={faSearch} size="xs" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); refreshFiles(true); }} title="Refresh">
            <FontAwesomeIcon icon={faSync} size="xs" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); startRootFolder(); }} title="New folder">
            <FontAwesomeIcon icon={faFolderPlus} size="xs" />
          </IconButton>
          {uploading ? (
            <CircularProgress size={16} />
          ) : (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} title="Upload file">
              <FontAwesomeIcon icon={faUpload} size="xs" />
            </IconButton>
          )}
        </ButtonGroup>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleUpload} />
      </SectionHeader>

      {!collapsed && showFilter && (
        <FilterRow>
          <FilterInput
            ref={filterInputRef}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter files..."
            onKeyDown={(e) => { if (e.key === 'Escape') { setShowFilter(false); setFilterText(''); } }}
          />
          {filterText && (
            <IconButton size="small" onClick={() => setFilterText('')}>
              <FontAwesomeIcon icon={faTimes} size="xs" />
            </IconButton>
          )}
        </FilterRow>
      )}

      {!collapsed && (
        <>
          {error && <ErrorText>{error}</ErrorText>}

          {state.loadingFiles && (state.fileTree.length === 0 || manualRefresh) && (
            <LoadingRow>
              <CircularProgress size={14} />
            </LoadingRow>
          )}

          {creatingRoot && (
            <RootCreateRow>
              <FontAwesomeIcon icon={faFolderPlus} style={{ fontSize: 12, color: '#f9a825', marginRight: 6 }} />
              <RootInput
                ref={rootInputRef}
                value={rootFolderName}
                onChange={(e) => setRootFolderName(e.target.value)}
                onKeyDown={handleRootFolderKey}
                onBlur={handleRootFolderCreate}
                placeholder="folder name"
              />
            </RootCreateRow>
          )}

          {pendingDelete && (
            <InlineConfirmDelete
              itemName={pendingDelete.path.split('/').pop() || pendingDelete.path}
              onConfirm={confirmDelete}
              onCancel={() => setPendingDelete(null)}
            />
          )}

          {!state.loadingFiles && state.fileTree.length === 0 && !creatingRoot && (
            <EmptyText>No files. Drop files here or <EmptyAction onClick={() => fileInputRef.current?.click()}>upload</EmptyAction></EmptyText>
          )}

          {sortNodes(filterNodes(state.fileTree, filterText)).map((node) => (
            <FileTreeNode key={node.path} node={node} {...treeProps} />
          ))}
        </>
      )}

      {dragging && <DropOverlay>Drop files here</DropOverlay>}
    </DropZone>
  );
};

export default FilesPanel;

const DropZone = styled.div<{ $active: boolean }>`
  position: relative;
  border: 2px dashed ${(p) => (p.$active ? '#1976d2' : 'transparent')};
  border-radius: 4px;
  transition: border-color 0.15s;
`;

const DropOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(25, 118, 210, 0.06);
  color: #1976d2;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
  border-radius: 4px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  min-height: 30px;
  padding: 2px 2px 2px 8px;
  background: #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: #e0e0e0;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 10px;
  color: #555;
  width: 10px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  color: #555;
  letter-spacing: 0.5px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  ${SectionHeader}:hover & {
    opacity: 1;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 4px 4px;
`;

const FilterInput = styled.input`
  font-size: 13px;
  border: 1px solid #bbb;
  border-radius: 3px;
  padding: 3px 6px;
  outline: none;
  flex: 1;
  min-width: 0;
  &:focus {
    border-color: #1976d2;
  }
`;

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 6px 0;
`;

const EmptyText = styled.div`
  font-size: 12px;
  color: #999;
  padding: 4px 0;
`;

const EmptyAction = styled.span`
  color: #1976d2;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.div`
  font-size: 11px;
  color: #e53935;
  padding: 2px 0 4px;
`;

const RootCreateRow = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 4px;
`;

const RootInput = styled.input`
  font-size: 13px;
  border: 1px solid #1976d2;
  border-radius: 2px;
  padding: 0 4px;
  outline: none;
  flex: 1;
  min-width: 0;
`;
